import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  GraduationCap,
  FileText,
  Video,
  ListChecks,
  Download,
  Loader2,
  CheckCircle,
  ExternalLink,
} from "lucide-react";
import { usePermissions } from "@/context/PermissionsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

async function downloadAuth(url, filename) {
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    toast.error("Download failed");
  }
}

export default function EmployeeOrientationTab({ employeeId, employeeName }) {
  const { hasPermission } = usePermissions();
  const canDownload = hasPermission("documents", "download");
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/orientation-history`);
      setHistory(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const downloadPacket = async (link) => {
    setDownloadingId(link.id);
    try {
      await downloadAuth(
        `${API}/orientation/${link.token}/packet.pdf`,
        `orientation-packet-${employeeName.replace(/\s+/g, "_")}-${link.id.slice(0, 6)}.pdf`
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadSignedDoc = async (link, doc) => {
    await downloadAuth(
      `${API}/orientation/${link.token}/document/${doc.id}/signed`,
      `${doc.title.replace(/\s+/g, "_")}-signed.pdf`
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!history || !history.links?.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="orientation-tab-empty">
        <GraduationCap className="w-8 h-8 text-slate-400 mx-auto mb-2" />
        <p className="text-slate-600">
          No orientation links generated for this employee yet.
        </p>
        <p className="text-sm text-slate-500 mt-1">
          Use the "Orientation" button above to generate one.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="orientation-tab">
      {history.links.map((link) => {
        const s = link.progress_summary || { percent: 0, done: 0, total: 0, is_complete: false };
        return (
          <div key={link.id} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-900">
                    {link.kind === "inservice" ? "In-service training" : "Orientation"} · {fmt(link.created_at)}
                  </h3>
                  {link.kind === "inservice" && (
                    <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 text-xs" data-testid={`link-kind-inservice-${link.id}`}>
                      {(link.inservice_titles || []).join(", ") || "Assigned in-services"}
                    </Badge>
                  )}
                  <Badge
                    variant="outline"
                    className={
                      s.is_complete
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"
                        : link.status === "in_progress"
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-slate-50 text-slate-700 border-slate-200"
                    }
                  >
                    {s.is_complete && <CheckCircle className="w-3 h-3" />}
                    {s.is_complete ? "Completed" : link.status.replace("_", " ")}
                  </Badge>
                  {link.completed_at && (
                    <span className="text-xs text-slate-500">
                      · Finished {fmt(link.completed_at)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-3 max-w-md">
                  <Progress value={s.percent} className="h-2 flex-1" />
                  <span className="text-xs text-slate-600 whitespace-nowrap">
                    {s.done}/{s.total} · {s.percent}%
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(link.public_url, "_blank")}
                  className="gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Open Link
                </Button>
                {canDownload && link.kind !== "inservice" && (
                <Button
                  size="sm"
                  onClick={() => downloadPacket(link)}
                  disabled={downloadingId === link.id}
                  className="gap-1"
                  data-testid={`download-packet-${link.id}`}
                >
                  {downloadingId === link.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3" />
                  )}
                  Download Packet PDF
                </Button>
                )}
              </div>
            </div>

            {(link.videos.length > 0 || link.documents.length > 0 || link.checklist.length > 0) && (
              <div className="grid gap-4 mt-5 md:grid-cols-3">
                {/* Videos */}
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-2">
                    <Video className="w-4 h-4 text-purple-600" />
                    Videos Watched
                  </div>
                  {link.videos.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">None yet</p>
                  ) : (
                    <ul className="space-y-1 text-xs">
                      {link.videos.map((v) => (
                        <li key={v.id} className="text-slate-700">
                          <div className="font-medium truncate">{v.title}</div>
                          <div className="text-slate-500">{fmt(v.watched_at)}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Documents */}
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    Signed Documents
                  </div>
                  {link.documents.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">None yet</p>
                  ) : (
                    <ul className="space-y-1.5 text-xs">
                      {link.documents.map((d) => (
                        <li key={d.id} className="text-slate-700 flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium truncate">
                              {d.title}
                              {d.kind === "inservice" && <span className="ml-1 rounded bg-teal-50 text-teal-700 border border-teal-200 px-1 text-[10px] font-normal" data-testid={`history-inservice-${d.id}`}>In-service{d.hours ? ` · ${d.hours} h` : ""} · filed to HR</span>}
                            </div>
                            <div className="text-slate-500">
                              {d.ack_only ? "Acknowledged by " : "Signed by "}
                              {d.signer_name} · {fmt(d.signed_at)}
                            </div>
                          </div>
                          {d.has_signed_copy && canDownload && (
                            <button
                              onClick={() => downloadSignedDoc(link, d)}
                              className="text-blue-600 hover:underline whitespace-nowrap flex-shrink-0"
                              data-testid={`download-signed-${d.id}`}
                            >
                              <Download className="w-3 h-3 inline" /> PDF
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Checklist */}
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-2">
                    <ListChecks className="w-4 h-4 text-emerald-600" />
                    Checklist
                  </div>
                  {link.checklist.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">None yet</p>
                  ) : (
                    <ul className="space-y-1 text-xs">
                      {link.checklist.map((c) => (
                        <li key={c.id} className="text-slate-700">
                          <div className="font-medium truncate">{c.title}</div>
                          <div className="text-slate-500">{fmt(c.checked_at)}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
